import React, { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchInfinite, fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import Button from '@dashboard/components/Button';
import Spinner from '@dashboard/components/Spinner/Spinner';
import Badge from '@dashboard/components/Badge';
import { format } from 'date-fns';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { stringify } from 'query-string';
import { integrationEventsKey } from '@dashboard/lib/queryKeys';

function getJsonResponse(response) {
  try {
    return JSON.stringify(JSON.parse(response), null, 2);
  } catch {
    return response;
  }
}

function getStatusBadgeAppearance(deliveryStatus) {
  switch (deliveryStatus) {
    case 'succeeded':
      return 'success';
    case 'failed':
      return 'error';
    case 'pending':
    default:
      return 'info';
  }
}

function EventsLogItem({ log, hideResend = false }) {
  const { token } = useAuth();
  const [showEvent, setShowEvent] = useState(false);
  const { emitToast } = useToast();
  const queryClient = useQueryClient();

  const response = getJsonResponse(log.response);

  const resendIntegrationMutation = useMutation({
    mutationFn: async () =>
      fetcher(`/integrations/${log.integrationId}/logs/${log._id}/resend`, token, {
        method: 'POST',
      }),
    onSuccess: result => {
      if (result.statusCode === 200) {
        emitToast({
          title: 'Event resent',
        });
      } else {
        emitToast({
          title: 'Resend failed',
          type: 'error',
        });
      }
    },
    onError: () => {
      emitToast({
        title: 'Resend failed',
        type: 'error',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: integrationEventsKey(log.integrationId) });
    },
  });

  return (
    <>
      <tr
        key={log._id}
        className="border-b cursor-pointer"
        onClick={() => setShowEvent(!showEvent)}
      >
        <td className="px-4 py-2 flex gap-x-2">
          {showEvent ? (
            <ChevronDownIcon className="w-4 h-4" />
          ) : (
            <ChevronRightIcon className="w-4 h-4" />
          )}
          <Badge appearance={getStatusBadgeAppearance(log.deliveryStatus)}>
            {log.deliveryStatus}
          </Badge>
        </td>
        <td className="px-4 py-2">{log._id}</td>
        <td className="px-4 py-2">{format(new Date(log.createdAt), 'd MMM, yyyy, HH:mm')}</td>
        <td className="px-4 py-2">{log.retries}</td>
        {!hideResend && (
          <td className="px-4 py-2 ml-auto text-right">
            <Button
              size="small"
              appearance="secondary"
              onClick={event => {
                event.stopPropagation();
                resendIntegrationMutation.mutate();
              }}
              disabled={resendIntegrationMutation.isLoading}
            >
              Resend
              {resendIntegrationMutation.isLoading && (
                <Spinner className="ml-2 h-5 w-5 !text-current" />
              )}
            </Button>
          </td>
        )}
      </tr>

      {showEvent && (
        <>
          <tr>
            <td colSpan="1" className="px-4 py-2">
              HTTP status code
            </td>
            <td colSpan={hideResend ? '3' : '4'} className="px-4 py-2">
              {log.statusCode}
            </td>
          </tr>
          {log.apiKeyMetadata && (
            <>
              <tr>
                <td colSpan="1" className="px-4 py-2 align-top">
                  API request details
                </td>
                <td colSpan={hideResend ? '3' : '4'} className="px-4 py-2">
                  <div className="text-xs space-y-1">
                    {log.apiKeyMetadata.method && (
                      <div>
                        <span className="font-medium">Method:</span> {log.apiKeyMetadata.method}
                      </div>
                    )}
                    {log.apiKeyMetadata.endpoint && (
                      <div>
                        <span className="font-medium">Endpoint:</span> {log.apiKeyMetadata.endpoint}
                      </div>
                    )}
                    {log.apiKeyMetadata.ipAddress && (
                      <div>
                        <span className="font-medium">IP address:</span>{' '}
                        {log.apiKeyMetadata.ipAddress}
                      </div>
                    )}
                    {log.apiKeyMetadata.userAgent && (
                      <div>
                        <span className="font-medium">User agent:</span>{' '}
                        {log.apiKeyMetadata.userAgent}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            </>
          )}
          <tr>
            <td colSpan="1" className="px-4 py-2 align-top">
              Payload
            </td>
            <td colSpan={hideResend ? '3' : '4'} className="px-4 py-2">
              <pre className="text-xs">{log.payload && JSON.stringify(log.payload, null, 2)}</pre>
            </td>
          </tr>
          <tr>
            <td colSpan="1" className="px-4 py-2 align-top">
              Response
            </td>
            <td colSpan={hideResend ? '3' : '4'} className="px-4 py-2">
              <pre className="text-xs">{response}</pre>
            </td>
          </tr>
        </>
      )}
    </>
  );
}

export default function EventsLog({ integrationId, hideResend = false }) {
  const { token } = useAuth();

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: integrationEventsKey(integrationId),
    queryFn: async ({ pageParam }) => {
      return fetchInfinite(
        `/integrations/${integrationId}/logs?${stringify({
          limit: 10,
          skip: pageParam * 10,
        })}`,
        token,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
    enabled: !!integrationId,
  });

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return <div>Unable to show events. Please refresh or contact support.</div>;
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center gap-y-2">
        <div className="text-gray-500">No events yet</div>
      </div>
    );
  }

  const hasMore = !!data?.pages?.[data?.pages?.length - 1]?.has_more;

  return (
    <table className="border-collapse table-auto w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="border-r font-normal px-4 py-2 text-gray-400 text-left">Status</th>
          <th className="border-r font-normal px-4 py-2 text-gray-400 text-left">Event ID</th>
          <th className="border-r font-normal px-4 py-2 text-gray-400 text-left">Date</th>
          <th className="font-normal px-4 py-2 text-gray-400 text-left">Retries</th>
          {!hideResend && <th className="font-normal px-4 py-2 text-gray-400 text-left"></th>}
        </tr>
      </thead>
      <tbody>
        {data?.pages?.map(data =>
          data?.data.map(log => <EventsLogItem log={log} key={log._id} hideResend={hideResend} />),
        )}

        {isFetchingNextPage && (
          <tr>
            <td colSpan={hideResend ? 4 : 5} className="px-4 py-2">
              <Spinner />
            </td>
          </tr>
        )}

        {!isFetchingNextPage && hasMore && (
          <tr>
            <td colSpan={hideResend ? 4 : 5} className="px-4 py-2">
              <Button onClick={fetchNextPage} appearance="secondary">
                Load more
              </Button>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
